import { Form, Input, InputNumber, Table, Modal, Button } from 'antd';
import type { ColumnType, TableProps } from 'antd/lib/table';
import { useStoreActions, useStoreState } from '../../store';
import { useEffect, useState } from 'react';
import type { TntJob, TntJobV2 } from '../../store/services/TimeInTransitService/types';
import { ShipiumTag } from '../shared/ShipiumTag';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import { hashTNTSearch } from '../../store/timeInTransit';
import { jobFailureMessage } from './tntUploadError';
import axios from 'axios';
import getGatewayUri from '../../store/services/getGatewayUri';

export interface TntCarrierDefaultTableProps {
  data: TntJobV2[];
  loading?: boolean;
  tableProps?: TableProps<TntJobV2>;
  removeColumns: string[];
  carrierId: string;
}

interface EditableColumnType<T> extends ColumnType<T> {
  editable?: boolean;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'number' | 'text';
  record: TntJobV2;
  index: number;
}

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}: EditableCellProps) => {
  const inputNode = inputType === 'number' ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export const jobUploadStatusToTag: Record<TntJob['uploadJobStatus'], React.ReactNode> = {
  IN_PROGRESS: (
    <ShipiumTag dark={true} color={'Blue'}>
      <ClockCircleOutlined style={{ paddingRight: '5px' }} />
      In Progress
    </ShipiumTag>
  ),
  COMPLETE: (
    <ShipiumTag dark={true} color={'Teal'}>
      <CheckCircleOutlined style={{ paddingRight: '5px' }} />
      Processed
    </ShipiumTag>
  ),
  FAILED: (
    <ShipiumTag color="Coral" dark={true}>
      <ExclamationCircleOutlined style={{ paddingRight: '5px' }} />
      Failed
    </ShipiumTag>
  ),
};

export const tntJobStatusPriority: TntJob['uploadJobStatus'][] = ['IN_PROGRESS', 'COMPLETE', 'FAILED'];

function tntJobStatusSort(a: TntJobV2, b: TntJobV2) {
  const aStatus = a.uploadJobStatus || 'COMPLETE';
  const bStatus = b.uploadJobStatus || 'COMPLETE';

  const aPriority = tntJobStatusPriority.indexOf(aStatus) + 1;
  const bPriority = tntJobStatusPriority.indexOf(bStatus) + 1;

  return aPriority - bPriority;
}

export const TntCarrierDefaultTable = ({ data, loading, tableProps, removeColumns }: TntCarrierDefaultTableProps) => {
  const fetchUsers = useStoreActions((actions) => actions.users.fetchUsers);
  const [form] = Form.useForm();
  const [updatedData, setUpdatedData] = useState(data);
  const [modal, contextHolder] = Modal.useModal();
  const userMap = useStoreState((state) => state.users.users);
  const downloadTntRecordsBySearch = useStoreActions((actions) => actions.timeInTransit.downloadTntRecordsBySearchV2);
  const downloadingTnt = useStoreState((state) => state.timeInTransit.downloadingTnt);

  const downloadErrorFile = (row: TntJobV2) => {
    try {
      axios({
        method: 'POST',
        url: `${getGatewayUri()}/downloadTntErrorFile`,
        data: {
          tntJobId: row.uploadJobId,
        },
        withCredentials: true,
      }).then((val) => {
        const link = document.createElement('a');
        link.setAttribute('href', val.data.downloadUrl);
        link.setAttribute('download', `${row.tntDataSetName}-${row.tntDataSetVersionId}-Error`);
        document.body.appendChild(link);
        link.click();
      });
    } catch (error) {
      modal.error({
        title: 'Failed',
        content: 'Failed to download error file.',
      });
    }
  };

  useEffect(() => {
    setUpdatedData(data);
  }, [data]);

  useEffect(() => {
    fetchUsers(undefined);
  }, [fetchUsers]);

  const columns: EditableColumnType<TntJobV2>[] = [
    {
      title: 'Name',
      dataIndex: 'tntDataSetName',
      editable: true,
      sorter: (a: TntJobV2, b: TntJobV2) => {
        return (a.tntDataSetName ?? '').localeCompare(b.tntDataSetName ?? '');
      },
    },
    {
      title: 'Carrier',
      dataIndex: 'carrierId',
      editable: true,
      sorter: (a: TntJobV2, b: TntJobV2) => {
        return (a.carrierId ?? '').localeCompare(b.carrierId ?? '');
      },
    },
    {
      title: 'Description',
      dataIndex: 'tntDataSetDescription',
      editable: true,
      sorter: (a: TntJobV2, b: TntJobV2) => {
        return (a.tntDataSetDescription ?? '').localeCompare(b.tntDataSetDescription ?? '');
      },
    },

    {
      title: 'Version',
      dataIndex: 'versionNumber',
      editable: true,
      sorter: (a: TntJobV2, b: TntJobV2) => {
        return (a.versionNumber?.toString() ?? '').localeCompare(b.versionNumber?.toString() ?? '');
      },
    },

    {
      title: 'Created by',
      sorter: (a: TntJobV2, b: TntJobV2) => {
        const userA = userMap[a.createdBy]?.givenName + userMap[a.createdBy]?.surname;
        const userB = userMap[b.createdBy]?.givenName + userMap[b.createdBy]?.surname;
        return userA.localeCompare(userB);
      },
      render: (row: TntJobV2) => {
        const uploadUser = userMap[row.createdBy];
        return <>{uploadUser ? `${uploadUser.givenName} ${uploadUser.surname}` : 'Unknown'}</>;
      },
    },

    {
      title: 'Created',
      defaultSortOrder: 'descend',
      sorter: (a: TntJobV2, b: TntJobV2) => new Date(a.createdAt ?? 0).valueOf() - new Date(b.createdAt ?? 0).valueOf(),
      render: (row: TntJobV2) => {
        const timestamp = row.createdAt;
        return (
          <>
            {timestamp
              ? new Date(timestamp).toLocaleDateString('sv-SE') +
                ' ' +
                new Date(timestamp).toLocaleString('en-US').split(', ')[1]
              : ''}
          </>
        );
      },
    },

    {
      title: 'Status',
      width: '150px',
      sorter: tntJobStatusSort,
      render: (row: TntJobV2) => {
        const status = row.uploadJobStatus || 'COMPLETE';
        return (
          <div
            style={{ display: 'flex', justifyContent: 'center' }}
            onClick={() => {
              row.uploadJobStatus === 'FAILED' &&
                modal.error({
                  title: 'Upload failed',
                  content: jobFailureMessage(row),
                });
            }}
          >
            {jobUploadStatusToTag[status]}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      width: '150px',
      render: (row: TntJobV2) => {
        const tntSearch = { dataSetId: row.tntDataSetId, dataSetVersionId: row.tntDataSetVersionId };
        // trim and replace whitespace

        const filename = (row.tntDataSetName ?? '').trim().replace(/ +/g, '-');
        const hash = hashTNTSearch(tntSearch);

        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '5px' }}>
            <Button
              title="Download Snapshot"
              loading={downloadingTnt[hash]}
              onClick={() => downloadTntRecordsBySearch({ name: filename, tntSearch })}
            >
              Snapshot <CloudDownloadOutlined />
            </Button>
            <Button
              title="Download Error"
              //loading={downloadingTnt[hash]}
              onClick={() => downloadErrorFile(row)}
            >
              Error <CloudDownloadOutlined />
            </Button>
          </div>
        );
      },
    },
  ];

  const filteredColumns: any = columns
    .filter((col) => !removeColumns.includes(col.title?.toString().toLowerCase() || ''))
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: TntJobV2) => ({
          record,
          inputType: 'text',
          dataIndex: col?.dataIndex || '',
          title: col?.title || '',
        }),
      };
    });

  return (
    <Form form={form} component={false}>
      <Table
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          size: 'small',
        }}
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        size="small"
        loading={loading}
        columns={filteredColumns}
        dataSource={updatedData}
        rowKey={(record) => record.uploadJobId}
        {...tableProps}
      />
      {contextHolder}
    </Form>
  );
};
